import { useState } from 'react';

import { RiArrowRightSLine } from '@remixicon/react';
import classNames from 'classnames';

import { Text } from '@spotify-internal/encore-web';
import {
  AiLoadingIndicator,
  ShimmerText,
} from '@spotify/confidence-core-react';

import { ToolGroupEntry } from '../../extensions/tool-extension';
import { DisplayItem } from '../../hooks/groupChatEvents';
import { MarkdownContent } from '../MarkdownContent';
import { ToolCallGroup } from '../ToolCallGroup';

import styles from './ThoughtProcess.module.css';

export type AgentProgressItem =
  | { type: 'thinking'; content: string; key: string }
  | { type: 'tool-group'; entries: ToolGroupEntry[]; key: string };

const getDisplayItemContent = (item: DisplayItem): string | null => {
  if (item.type === 'tool-group') return null;
  if (item.event.thinking) return item.event.thinking.content;
  if (item.event.assistantMessage) return item.event.assistantMessage.content;
  return null;
};

export const toAgentProgressItems = (
  items: DisplayItem[],
): AgentProgressItem[] =>
  items.flatMap<AgentProgressItem>(item => {
    if (item.type === 'tool-group') return [item];
    const content = getDisplayItemContent(item);
    return content ? [{ type: 'thinking', content, key: item.key }] : [];
  });

export const toThinkingProgressItems = (
  messages: string[],
): AgentProgressItem[] =>
  messages.map((content, index) => ({
    type: 'thinking',
    content,
    key: `thinking-${index}-${content}`,
  }));

export const AgentProgressItems = ({
  items,
  shimmerLatestThinking,
  collapseConsecutiveCalls = true,
}: {
  items: AgentProgressItem[];
  shimmerLatestThinking: boolean;
  collapseConsecutiveCalls?: boolean;
}) => {
  const lastThinkingIndex = items.findLastIndex(
    item => item.type === 'thinking',
  );

  return (
    <>
      {items.map((item, index) => {
        if (item.type === 'tool-group') {
          return (
            <ToolCallGroup
              key={item.key}
              entries={item.entries}
              variant="inline"
              collapseConsecutiveCalls={collapseConsecutiveCalls}
            />
          );
        }

        const hasListContent = /^(\s*[-*]|\s*\d+\.)\s/m.test(item.content);
        const isActive =
          index === lastThinkingIndex &&
          shimmerLatestThinking &&
          !hasListContent;
        return (
          <ShimmerText key={item.key} active={isActive}>
            <MarkdownContent>{item.content}</MarkdownContent>
          </ShimmerText>
        );
      })}
    </>
  );
};

const COLLAPSE_THRESHOLD = 8;
const VISIBLE_TAIL_COUNT = 4;

const StepsToggle = ({
  expanded,
  stepCount,
  onClick,
}: {
  expanded: boolean;
  stepCount: number;
  onClick: () => void;
}) => (
  <button
    type="button"
    className={classNames(styles.header, styles.stepsToggle)}
    aria-expanded={expanded}
    onClick={onClick}
   data-track-source-anchor="006f225485;steps-toggle-button">
    <RiArrowRightSLine
      size={16}
      className={classNames(styles.arrow, {
        [styles.arrowExpanded]: expanded,
      })}
    />
    <Text semanticColor="textSubdued" variant="bodyMedium">
      {stepCount} earlier {stepCount === 1 ? 'step' : 'steps'}
    </Text>
  </button>
);

export const AgentProgress = ({
  items,
  isInterrupted = false,
  collapseThreshold = COLLAPSE_THRESHOLD,
  collapseConsecutiveCalls = true,
}: {
  items: AgentProgressItem[];
  isInterrupted?: boolean;
  collapseThreshold?: number;
  collapseConsecutiveCalls?: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);
  const visibleItems = items.filter(
    item => item.type === 'tool-group' || Boolean(item.content),
  );

  if (visibleItems.length === 0) return <AiLoadingIndicator />;

  const isCollapsible = visibleItems.length >= collapseThreshold;
  const visibleTailCount = Math.min(VISIBLE_TAIL_COUNT, collapseThreshold - 1);
  const isCollapsed = isCollapsible && !expanded;

  if (isCollapsed) {
    const hiddenCount = visibleItems.length - visibleTailCount;
    const tailItems = visibleItems.slice(-visibleTailCount);
    return (
      <div className={styles.activeRoot}>
        <StepsToggle
          expanded={false}
          stepCount={hiddenCount}
          onClick={() => setExpanded(true)}
         data-track-source-anchor="1c82ffd06a;agent-progress-steps-toggle"/>
        <AgentProgressItems
          items={tailItems}
          shimmerLatestThinking={!isInterrupted}
          collapseConsecutiveCalls={collapseConsecutiveCalls}
        />
      </div>
    );
  }

  return (
    <div className={styles.activeRoot}>
      {isCollapsible && (
        <StepsToggle
          expanded
          stepCount={visibleItems.length - visibleTailCount}
          onClick={() => setExpanded(false)}
         data-track-source-anchor="3bb02a7030;agent-progress-steps-toggle-2"/>
      )}
      <AgentProgressItems
        items={visibleItems}
        shimmerLatestThinking={!isInterrupted}
        collapseConsecutiveCalls={collapseConsecutiveCalls}
      />
    </div>
  );
};
